import { useMutation } from "@apollo/client/react";
import {
  ContentFragment,
  ContentStatus,
  ExamEditPageData,
  ExamEditPageDataVariables,
  ExamSyllabusFolderOptions,
  UpdateExam,
  UpdateExamVariables,
} from "__generated__/types";
import { useConfirmDialog } from "common/components/confirm-dialog/use-confirm-dialog";
import { AdministrationLayout } from "common/components/layouts/AdministrationLayout";
import { LoadingLayout } from "common/components/layouts/LoadingLayout";
import { NoDataError } from "common/components/NoDataError";
import { SelectOption } from "common/components/Select";
import { usePreviousPath } from "common/context/PreviousPathContext";
import { useSimpleQuery } from "common/graphql/useSimpleQuery";
import { markdownInput } from "common/input-types";
import { ADMIN_PREFIX, urls } from "common/urls";
import {
  ExamSyllabusForm,
  ExamSyllabusFormValues,
} from "domain/admin/exam-syllabus/ExamSyllabusForm";
import gql from "graphql-tag";
import React from "react";
import { useNavigate, useParams } from "react-router";

const GET_EXAM = gql`
  query ExamEditPageData($examId: ID!) {
    examSyllabusById(id: $examId) {
      examDate
      description {
        content
        isMarkdown
      }
      id
      title
      studyPoints
      status
      context {
        id
        title
      }
    }
  }
`;

const GET_FOLDER_OPTIONS = gql`
  query ExamSyllabusFolderOptions {
    folderList {
      id
      status
      title
    }
  }
`;

const MUTATION = gql`
  mutation UpdateExam($input: UpdateExamSyllabusDetailsInput!) {
    updateExamSyllabusDetails(input: $input) {
      examSyllabus {
        examDate
        description {
          content
          isMarkdown
        }
        id
        title
        studyPoints
        status
      }
    }
  }
`;

export const folderToSelectOption = (
  folder: ContentFragment, // TODO: Create own fragment?
): SelectOption => {
  return {
    key: folder.id,
    value: folder.id,
    label: folder.title,
  };
};

type RouteProps = {
  examId: string;
};

export const ExamEditPage: React.FC = () => {
  const { examId } = useParams<RouteProps>();

  const { showConfirm, ConfirmBox } = useConfirmDialog();
  const confirmRef = React.useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const previousPath = usePreviousPath();

  if (!examId) {
    throw new Error("Unexpected error: Missing examId");
  }

  const { data, loadingOrError } = useSimpleQuery<
    ExamEditPageData,
    ExamEditPageDataVariables
  >(GET_EXAM, {
    variables: {
      examId,
    },
  });
  const { data: folderData } =
    useSimpleQuery<ExamSyllabusFolderOptions>(GET_FOLDER_OPTIONS);
  const [mutate, { error, loading }] = useMutation<
    UpdateExam,
    UpdateExamVariables
  >(MUTATION);

  function exitEditPage() {
    if (previousPath && !previousPath.endsWith(`/${ADMIN_PREFIX}`)) {
      navigate(-1);
    } else {
      navigate(urls.admin);
    }
  }

  const onSubmit = (input: ExamSyllabusFormValues) => {
    const text =
      input.status === ContentStatus.PUBLISHED
        ? "Er du sikker på at du vil lagre detaljene og publisere " +
          "pensumet til studentene? Husk at du ikke vil publisere før " +
          "du er sikker på at innholdet i pensumet er helt klart."
        : "Er du sikker på at du vil lagre detaljene til pensumet?";

    showConfirm({
      text,
      okButtonText: "Ja, lagre endringer",
      okButtonAction: () => handleSubmitConfirmed(input),
      cancelButtonText: "Avbryt",
    });

    if (confirmRef.current) {
      confirmRef.current.scrollIntoView();
    }
  };

  const handleSubmitConfirmed = async (input: ExamSyllabusFormValues) => {
    const result = await mutate({
      variables: {
        input: {
          id: examId,
          status: { updatedValue: input.status },
          title: { updatedValue: input.title },
          studyPoints: { updatedValue: input.studyPoints },
          examDate: {
            updatedValue: input.examDate,
          },
          description: {
            updatedValue: markdownInput(input.description),
          },
        },
      },
    });

    if (result.data) {
      exitEditPage();
    }
  };

  if (loadingOrError) {
    return <LoadingLayout>{loadingOrError}</LoadingLayout>;
  }

  if (!data || !data.examSyllabusById) {
    return (
      <LoadingLayout>
        <NoDataError />
      </LoadingLayout>
    );
  }

  return (
    <AdministrationLayout
      title={data.examSyllabusById.title}
      context={data.examSyllabusById.context}
    >
      <ExamSyllabusForm
        onSubmit={onSubmit}
        onCancel={exitEditPage}
        canPublish={true}
        examSyllabus={data.examSyllabusById}
        loading={loading}
        error={error}
        options={
          folderData ? folderData.folderList?.map(folderToSelectOption) : null
        }
        submitText="Lagre"
      />
      {<div ref={confirmRef}>{ConfirmBox}</div>}
    </AdministrationLayout>
  );
};
